import { useEffect, useState } from 'react';

// Subscribe to a media query from React. The layout tier is normally a CSS
// concern and stays one — this exists for the cases where the DOM itself has to
// differ, not just its styling.
//
// The first of those (mobile-prototype-build): the global search panel must
// escape the top bar to be a full-height sheet. `.topbar` carries a
// backdrop-filter, which makes it the containing block for any `position: fixed`
// descendant — so a fixed panel inside it resolves `top/bottom` against a 42px
// box and comes out 2px tall. No stylesheet can undo that from the inside; the
// panel has to be portalled out, and a portal is a render decision.
//
// SSR-safe: `matchMedia` is read lazily and guarded.
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // the query may have changed between render and effect
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

// The DRAWER tier. Wider than the page-layout phone tier (767) on purpose: a
// modal is an overlay, and on anything narrower than a full desktop a
// two-column master-detail dialog leaves the pane cramped and the rail eating a
// third of the width. Freddi's own window is 961 — squarely in the band where
// the dialog was still being served (2026-08-11). Page layouts keep their own
// 767 boundary; this governs the drawers only.
export const PHONE_QUERY = '(max-width: 1023px)';
